import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StarIcon from '@mui/icons-material/Star';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ShareIcon from '@mui/icons-material/Share';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MovieCard from '../../components/MovieCard/MovieCard';
import { getMovieById, getPopularMovies } from '../../utils/database';
import './MovieDetailsPage.css';

const MovieDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [similarMovies, setSimilarMovies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showTrailer, setShowTrailer] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadMovie();
  }, [id]);

  const loadMovie = () => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      const movieData = getMovieById(id);
      if (movieData) {
        setMovie(movieData);
        // Get similar movies (for demo, using popular movies)
        setSimilarMovies(getPopularMovies().filter(m => m.id !== movieData.id).slice(0, 6));
      }
      setIsLoading(false);
    }, 500);
  };

  if (isLoading) {
    return (
      <div className="movie-details__loading">
        <div className="movie-details__spinner"></div>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="movie-details__error">
        <h2>Movie not found</h2>
        <button onClick={() => navigate('/')}>Go Home</button>
      </div>
    );
  }

  const extractYoutubeId = (url) => {
    const match = url.match(/[?&]v=([^&]+)/);
    return match ? match[1] : null;
  };

  return (
    <div className="movie-details">
      {/* Hero Section */}
      <motion.section 
        className="movie-details__hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="movie-details__backdrop">
          <img 
            src={movie.backdrop || movie.poster} 
            alt={movie.title}
            className="movie-details__backdrop-image"
          />
          <div className="movie-details__backdrop-overlay"></div>
        </div>

        <button 
          className="movie-details__back"
          onClick={() => navigate(-1)}
        >
          <ArrowBackIcon />
        </button>

        <div className="movie-details__hero-content">
          <motion.div 
            className="movie-details__poster-wrapper"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <img 
              src={movie.poster} 
              alt={movie.title}
              className="movie-details__poster"
            />
            {movie.isNew && (
              <span className="movie-details__badge">NEW</span>
            )}
          </motion.div>

          <motion.div 
            className="movie-details__info"
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h1 className="movie-details__title">{movie.title}</h1>
            
            <div className="movie-details__meta">
              <div className="movie-details__rating">
                <StarIcon />
                <span>{movie.rating}</span>
                <span className="movie-details__votes">({movie.votes} votes)</span>
              </div>
              <span className="movie-details__year">{movie.year}</span>
              <span className="movie-details__duration">{movie.duration}</span>
              <span className="movie-details__language">{movie.language}</span>
            </div>

            <div className="movie-details__genres">
              {movie.categories.map((category) => (
                <span key={category} className="movie-details__genre">
                  {category}
                </span>
              ))}
            </div>

            <p className="movie-details__description">
              {movie.fullDescription}
            </p>

            <div className="movie-details__actions">
              <button 
                className="movie-details__button movie-details__button--primary"
                onClick={() => setShowTrailer(true)}
              >
                <PlayArrowIcon />
                Watch Trailer
              </button>
              <button className="movie-details__button movie-details__button--secondary">
                <PlayArrowIcon />
                Play Movie
              </button>
              <button className="movie-details__button movie-details__button--icon">
                <BookmarkBorderIcon />
              </button>
              <button className="movie-details__button movie-details__button--icon">
                <ShareIcon />
              </button>
            </div>

            <div className="movie-details__crew">
              <div className="movie-details__crew-item">
                <span className="movie-details__crew-label">Director</span>
                <span className="movie-details__crew-value">{movie.director}</span>
              </div>
              <div className="movie-details__crew-item">
                <span className="movie-details__crew-label">Release Date</span>
                <span className="movie-details__crew-value">{movie.releaseDate}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Tabs Section */}
      <section className="movie-details__tabs-section">
        <div className="movie-details__tabs">
          <button 
            className={`movie-details__tab ${activeTab === 'overview' ? 'movie-details__tab--active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`movie-details__tab ${activeTab === 'cast' ? 'movie-details__tab--active' : ''}`}
            onClick={() => setActiveTab('cast')}
          >
            Cast & Crew
          </button>
          <button 
            className={`movie-details__tab ${activeTab === 'details' ? 'movie-details__tab--active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
        </div>

        <motion.div 
          className="movie-details__tab-content"
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'overview' && (
            <div className="movie-details__overview">
              <h3>Synopsis</h3>
              <p>{movie.fullDescription}</p>
              
              <div className="movie-details__stats">
                <div className="movie-details__stat">
                  <span className="movie-details__stat-label">Box Office</span>
                  <span className="movie-details__stat-value">{movie.boxOffice}</span>
                </div>
                <div className="movie-details__stat">
                  <span className="movie-details__stat-label">Budget</span>
                  <span className="movie-details__stat-value">{movie.budget}</span>
                </div>
                <div className="movie-details__stat">
                  <span className="movie-details__stat-label">IMDb Rating</span>
                  <span className="movie-details__stat-value">⭐ {movie.rating}/10</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'cast' && (
            <div className="movie-details__cast">
              <h3>Cast</h3>
              <div className="movie-details__cast-grid">
                {movie.cast.map((actor) => (
                  <div key={actor} className="movie-details__cast-member">
                    <div className="movie-details__cast-avatar">
                      {actor.charAt(0)}
                    </div>
                    <span>{actor}</span>
                  </div>
                ))}
              </div>
              
              <h3 style={{ marginTop: '2rem' }}>Director</h3>
              <p>{movie.director}</p>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="movie-details__extra">
              <div className="movie-details__detail-item">
                <span className="movie-details__detail-label">Original Title</span>
                <span className="movie-details__detail-value">{movie.title}</span>
              </div>
              <div className="movie-details__detail-item">
                <span className="movie-details__detail-label">Release Date</span>
                <span className="movie-details__detail-value">{movie.releaseDate}</span>
              </div>
              <div className="movie-details__detail-item">
                <span className="movie-details__detail-label">Duration</span>
                <span className="movie-details__detail-value">{movie.duration}</span>
              </div>
              <div className="movie-details__detail-item">
                <span className="movie-details__detail-label">Language</span>
                <span className="movie-details__detail-value">{movie.language}</span>
              </div>
              <div className="movie-details__detail-item">
                <span className="movie-details__detail-label">Budget</span>
                <span className="movie-details__detail-value">{movie.budget}</span>
              </div>
              <div className="movie-details__detail-item">
                <span className="movie-details__detail-label">Revenue</span>
                <span className="movie-details__detail-value">{movie.boxOffice}</span>
              </div>
              <div className="movie-details__detail-item">
                <span className="movie-details__detail-label">Genres</span>
                <span className="movie-details__detail-value">{movie.categories.join(', ')}</span>
              </div>
            </div>
          )}
        </motion.div>
      </section>

      {/* Similar Movies */}
      <section className="movie-details__similar">
        <h2 className="movie-details__similar-title">You May Also Like</h2>
        <div className="movie-details__similar-grid">
          {similarMovies.map((movie, index) => (
            <MovieCard 
              key={movie.id}
              movie={movie}
              index={index}
              variant="poster"
            />
          ))}
        </div>
      </section>

      {/* Trailer Modal */}
      {showTrailer && movie.trailerUrl && (
        <motion.div 
          className="movie-details__trailer-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowTrailer(false)}
        >
          <div className="movie-details__trailer-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="movie-details__trailer-close"
              onClick={() => setShowTrailer(false)}
            >
              ×
            </button>
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${extractYoutubeId(movie.trailerUrl)}?autoplay=1`}
              title={`${movie.title} Trailer`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default MovieDetailsPage;